"use client"

/**
 * MollieCheckout — Custom checkout using Mollie Components + Express buttons
 *
 * Architecture:
 *   1. Load Mollie JS from CDN (mollie.com/v1/mollie.js)
 *   2. Mount card components (cardNumber, cardHolder, expiryDate, verificationCode)
 *   3. Offer Apple Pay, PayPal, Google Pay, and local methods as optional buttons
 *   4. Card: tokenize → POST /api/checkout/mollie/create (with cardToken)
 *   5. Apple Pay: validate merchant → create payment with applePayToken
 *   6. PayPal / Google Pay / local: create payment → redirect to checkoutUrl
 *   7. Poll /api/checkout/mollie/status after redirect return or after card submit
 *
 * Subscription double-charge prevention:
 *   - Backend sets startDate on the Mollie subscription = today + plan.days
 *   - Initial payment covers current period; next charge is after it ends
 */

import { useState, useEffect, useRef, useCallback } from "react"
import { GlowCard } from "@/components/crystality/glow-card"
import { Button } from "@/components/ui/button"
import {
  Loader2,
  Check,
  X,
  ChevronLeft,
  CreditCard,
  Shield,
  Lock,
  Clock,
  RefreshCw,
  AlertCircle,
} from "lucide-react"
import { cn } from "@/lib/utils"

// ── Types ──────────────────────────────────────────────────────────────────

interface MollieCheckoutProps {
  email: string
  planId: string
  planName: string
  priceGbp: number
  referralCode?: string
  onComplete: (licenseKey: string, orderId: string) => void
  onCancel: () => void
  onError: (error: string) => void
  onSwitchToCrypto?: () => void
}

type CheckoutStep = "initializing" | "ready" | "submitting" | "processing" | "redirecting" | "success" | "failed"
type ExpressMethod = "applepay" | "paypal" | "googlepay"

interface MollieMethod {
  id: string
  description: string
  image: string | null
}

// Express method labels & icons (SVGs kept minimal for bundle size)
const EXPRESS_METHODS: Array<{ id: ExpressMethod; label: string; icon: React.ReactNode }> = [
  {
    id: "applepay",
    label: "Apple Pay",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
    ),
  },
  {
    id: "paypal",
    label: "PayPal",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.04c-.564 0-1.04.407-1.13.963l-1.11 7.04a.641.641 0 0 1-.634.536l-.09-.533z"/>
      </svg>
    ),
  },
  {
    id: "googlepay",
    label: "Google Pay",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
        <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 1 1 0-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0 0 12.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
      </svg>
    ),
  },
]

// ── Constants ──────────────────────────────────────────────────────────────

const POLLING_INTERVAL_FAST = 1200
const POLLING_INTERVAL_SLOW = 2500
const POLLING_FAST_DURATION_MS = 30 * 1000
const MAX_POLLING_TIME_MS = 10 * 60 * 1000

declare global {
  interface Window {
    Mollie: (profileId: string, options: Record<string, any>) => any
  }
}

// ── Component ──────────────────────────────────────────────────────────────

export function MollieCheckout({
  email,
  planId,
  planName,
  priceGbp,
  referralCode,
  onComplete,
  onCancel,
  onError,
  onSwitchToCrypto,
}: MollieCheckoutProps) {
  const [step, setStep] = useState<CheckoutStep>("initializing")
  const [error, setError] = useState("")
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({})
  const [orderId, setOrderId] = useState<string | null>(null)
  const [localMethods, setLocalMethods] = useState<MollieMethod[]>([])
  const [elapsedTime, setElapsedTime] = useState(0)
  const [redirectMethod, setRedirectMethod] = useState<string | null>(null)

  // Refs
  const mollieRef = useRef<any>(null)
  const componentRefs = useRef<Record<string, any>>({})
  const initRef = useRef(false)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const elapsedTimerRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number>(0)
  const pollingSlowedRef = useRef(false)
  const orderIdRef = useRef<string | null>(null)

  const profileId = process.env.NEXT_PUBLIC_MOLLIE_PROFILE_ID || ""
  const isTestMode = process.env.NEXT_PUBLIC_MOLLIE_TEST_MODE === "true"

  // ── Load available payment methods ──────────────────────────────────────
  useEffect(() => {
    // Fetch local payment methods from Mollie
    fetch(`/api/checkout/mollie/methods?planId=${planId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.methods) {
          // Filter out card, applepay, paypal, googlepay — handled separately
          const excludeIds = ["creditcard", "applepay", "paypal", "googlepay"]
          const local = data.methods.filter((m: MollieMethod) => !excludeIds.includes(m.id))
          setLocalMethods(local)
        }
      })
      .catch(() => {})
  }, [planId])

  // ── Load Mollie.js from CDN ─────────────────────────────────────────────
  const loadMollieJs = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (typeof window !== "undefined" && "Mollie" in window && (window as any).Mollie) {
        resolve()
        return
      }
      const script = document.createElement("script")
      script.src = "https://js.mollie.com/v1/mollie.js"
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error("Failed to load Mollie.js"))
      document.head.appendChild(script)
    })
  }, [])

  // ── Initialize Mollie Components ────────────────────────────────────────
  useEffect(() => {
    if (initRef.current || !profileId) return
    initRef.current = true

    const initialize = async () => {
      try {
        await loadMollieJs()

        if (!window.Mollie) {
          throw new Error("Mollie.js failed to load")
        }

        const mollie = window.Mollie(profileId, {
          locale: "en_US",
          testmode: isTestMode,
        })
        mollieRef.current = mollie

        setStep("ready")

        // Mount card components after React renders the containers
        setTimeout(() => mountCardComponents(mollie), 150)
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to initialize checkout"
        setError(msg)
        setStep("failed")
      }
    }

    initialize()

    return () => {
      stopPolling()
      destroyComponents()
    }
  }, [profileId, isTestMode, loadMollieJs])

  // ── Mount Mollie Components ─────────────────────────────────────────────
  const mountCardComponents = useCallback((mollie: any) => {
    const componentMap: Record<string, string> = {
      cardNumber: "#mollie-card-number",
      cardHolder: "#mollie-card-holder",
      expiryDate: "#mollie-expiry-date",
      verificationCode: "#mollie-cvv",
    }

    const baseStyles = {
      base: {
        color: "#e2e8f0",
        fontSize: "16px",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontWeight: "400",
        "::placeholder": { color: "#64748b" },
      },
      invalid: {
        color: "#ef4444",
      },
      valid: {
        color: "#22c55e",
      },
    }

    for (const [name, selector] of Object.entries(componentMap)) {
      const el = document.querySelector(selector)
      if (!el) continue
      try {
        const comp = mollie.createComponent(name, { styles: baseStyles })
        comp.mount(selector)
        comp.addEventListener("change", (event: any) => {
          if (event.error) {
            setCardErrors((prev) => ({ ...prev, [name]: event.error }))
          } else {
            setCardErrors((prev) => {
              const next = { ...prev }
              delete next[name]
              return next
            })
          }
        })
        componentRefs.current[name] = comp
      } catch (e) {
        console.error(`[MOLLIE] Failed to mount ${name}:`, e)
      }
    }
  }, [])

  const destroyComponents = useCallback(() => {
    for (const comp of Object.values(componentRefs.current)) {
      try { comp?.unmount?.() } catch {}
    }
    componentRefs.current = {}
  }, [])

  // ── Re-mount card components when returning to "ready" step ─────────────
  // When the step leaves "ready" (e.g. "redirecting" for a local method error),
  // React unmounts the card form containers, destroying the Mollie iframes.
  // When step returns to "ready", the empty divs are re-rendered and we need
  // to re-mount the Mollie components into them.
  useEffect(() => {
    if (step === "ready" && mollieRef.current) {
      const timer = setTimeout(() => {
        destroyComponents()
        mountCardComponents(mollieRef.current)
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [step, mountCardComponents, destroyComponents])

  // ── Polling for payment status ──────────────────────────────────────────
  const startPolling = useCallback((oid: string) => {
    if (pollingRef.current) return
    orderIdRef.current = oid
    setOrderId(oid)
    startTimeRef.current = Date.now()
    pollingSlowedRef.current = false
    setStep("processing")

    const poll = async () => {
      const currentOrderId = orderIdRef.current
      if (!currentOrderId) return

      if (Date.now() - startTimeRef.current > MAX_POLLING_TIME_MS) {
        stopPolling()
        setStep("failed")
        setError("Payment confirmation timed out. Check your email for confirmation.")
        return
      }

      try {
        const res = await fetch(`/api/checkout/mollie/status?orderId=${encodeURIComponent(currentOrderId)}`)
        if (!res.ok) return
        const data = await res.json()

        if (data.status === "completed") {
          stopPolling()
          setStep("success")
          setTimeout(() => onComplete(data.licenseKey || "", currentOrderId), 2000)
          return
        }
        if (data.status === "failed") {
          stopPolling()
          setStep("failed")
          setError("Payment was declined. Please try again with a different payment method.")
          return
        }
        if (data.status === "expired") {
          stopPolling()
          setStep("failed")
          setError("Payment session expired. Please start over.")
          return
        }
        if (data.status === "cancelled") {
          stopPolling()
          setStep("failed")
          setError("Payment was cancelled.")
          return
        }
      } catch {
        // Network error — keep polling
      }
    }

    pollingRef.current = setInterval(poll, POLLING_INTERVAL_FAST)
    elapsedTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current
      setElapsedTime(Math.floor(elapsed / 1000))
      if (elapsed >= POLLING_FAST_DURATION_MS && !pollingSlowedRef.current) {
        pollingSlowedRef.current = true
        if (pollingRef.current) clearInterval(pollingRef.current)
        pollingRef.current = setInterval(poll, POLLING_INTERVAL_SLOW)
      }
    }, 1000)

    poll() // immediate
  }, [onComplete])

  const stopPolling = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null }
  }, [])

  // ── Card submit ─────────────────────────────────────────────────────────
  const handleCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mollieRef.current || step === "submitting") return

    setStep("submitting")
    setError("")

    try {
      // 1. Tokenize the card
      const { token, error: tokenError } = await mollieRef.current.createToken()
      if (tokenError) {
        setStep("ready")
        setError(tokenError.message || "Card validation failed. Please check your details.")
        return
      }
      if (!token) {
        setStep("ready")
        setError("Failed to tokenize card. Please try again.")
        return
      }

      // 2. Create payment on server
      const res = await fetch("/api/checkout/mollie/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, planId, cardToken: token, referralCode }),
      })
      const data = await res.json()

      if (!res.ok || !data.success) {
        setStep("ready")
        setError(data.error || "Payment creation failed. Please try again.")
        return
      }

      const newOrderId: string = data.orderId

      // 3a. If Mollie provides a checkoutUrl, the card requires 3DS → redirect
      if (data.checkoutUrl) {
        // Open 3DS verification in popup
        const popup = window.open(
          data.checkoutUrl,
          "crystality_3ds",
          "width=500,height=700,scrollbars=yes,resizable=yes,left=" +
            (window.screen.width / 2 - 250) + ",top=" + (window.screen.height / 2 - 350)
        )

        if (!popup || popup.closed) {
          // Popup blocked — fall back to redirect
          setRedirectMethod("card (3D Secure)")
          setStep("redirecting")
          try {
            localStorage.setItem("crystality_checkout", JSON.stringify({ email, planId, orderId: newOrderId }))
          } catch {}
          window.location.href = data.checkoutUrl
          return
        }

        // Poll while 3DS popup is open
        setStep("processing")
        const check3ds = setInterval(() => {
          if (popup.closed) clearInterval(check3ds)
        }, 500)
        startPolling(newOrderId)
        return
      }

      // 3b. If already paid (no 3DS needed)
      if (data.isPaid) {
        setStep("success")
        setTimeout(() => onComplete("", newOrderId), 2000)
        return
      }

      // 3c. Start polling (3DS in progress server-side, or just confirming)
      startPolling(newOrderId)
    } catch (err) {
      setStep("ready")
      setError(err instanceof Error ? err.message : "An unexpected error occurred")
    }
  }

  // ── Apple Pay ──────────────────────────────────────────────────────────
  // The Apple Pay JS SDK (loaded in layout.tsx) provides ApplePaySession on
  // ALL devices — on Safari/iOS it shows the native payment sheet, on other
  // browsers it shows Apple's built-in "Scan Code with iPhone" QR modal.
  // Falls back to Mollie hosted checkout if the SDK isn't available.
  const handleApplePay = async () => {
    const ApplePaySessionAny: any = (window as any).ApplePaySession

    // Fallback: if Apple Pay SDK didn't load, redirect via Mollie hosted checkout
    if (!ApplePaySessionAny) {
      await handleRedirectMethod("applepay", "Apple Pay")
      return
    }

    // Check version 3 support
    if (
      typeof ApplePaySessionAny.supportsVersion === "function" &&
      !ApplePaySessionAny.supportsVersion(3)
    ) {
      await handleRedirectMethod("applepay", "Apple Pay")
      return
    }

    const request = {
      countryCode: "GB",
      currencyCode: "GBP",
      merchantCapabilities: ["supports3DS"],
      supportedNetworks: ["amex", "maestro", "masterCard", "visa", "vPay"],
      requiredBillingContactFields: ["name", "email"],
      total: { label: planName, amount: priceGbp.toFixed(2) },
    }

    const session = new ApplePaySessionAny(3, request)

    session.onvalidatemerchant = async (event: any) => {
      try {
        const res = await fetch("/api/checkout/mollie/apple-pay-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ validationUrl: event.validationURL }),
        })
        if (!res.ok) throw new Error("Merchant validation failed")
        const sessionData = await res.json()
        session.completeMerchantValidation(sessionData)
      } catch (err) {
        session.abort()
        setError("Apple Pay merchant validation failed. Please try another payment method.")
      }
    }

    session.onpaymentauthorized = async (event: any) => {
      try {
        const applePayToken = JSON.stringify(event.payment.token)

        const res = await fetch("/api/checkout/mollie/create", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, planId, applePayToken, referralCode }),
        })
        const data = await res.json()

        if (!res.ok || !data.success) {
          session.completePayment(ApplePaySessionAny.STATUS_FAILURE)
          setError(data.error || "Apple Pay payment failed")
          return
        }

        session.completePayment(ApplePaySessionAny.STATUS_SUCCESS)
        startPolling(data.orderId)
      } catch (err) {
        session.completePayment(ApplePaySessionAny.STATUS_FAILURE)
        setError("Apple Pay payment failed. Please try again.")
      }
    }

    session.oncancel = () => {}
    session.begin()
  }

  // ── PayPal / Google Pay / Local Methods (redirect) ──────────────────────
  const handleRedirectMethod = async (method: string, label: string) => {
    setError("")
    setRedirectMethod(label)
    setStep("redirecting")

    try {
      const res = await fetch("/api/checkout/mollie/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, planId, method, referralCode }),
      })
      const data = await res.json()

      if (!res.ok || !data.success) {
        setStep("ready")
        setRedirectMethod(null)
        // If the payment method doesn't support subscriptions (e.g. iDEAL, Bancontact),
        // show a clear explanation instead of the raw Mollie error
        if (data.error === "METHOD_NO_SUBSCRIPTION") {
          setError(
            `${label} can only be used for one-time payments. To use ${label}, please select the Lifetime plan. For recurring subscriptions, use Card, PayPal, or Apple Pay.`
          )
        } else {
          setError(data.error || `${label} payment failed`)
        }
        return
      }

      if (data.checkoutUrl) {
        // Open payment in a popup window instead of full redirect
        const popup = window.open(
          data.checkoutUrl,
          "crystality_payment",
          "width=500,height=700,scrollbars=yes,resizable=yes,left=" +
            (window.screen.width / 2 - 250) + ",top=" + (window.screen.height / 2 - 350)
        )

        if (!popup || popup.closed) {
          // Popup blocked — fall back to redirect
          try {
            localStorage.setItem("crystality_checkout", JSON.stringify({ email, planId, orderId: data.orderId }))
          } catch {}
          window.location.href = data.checkoutUrl
          return
        }

        // Poll for payment completion while popup is open
        setStep("processing")
        setOrderId(data.orderId)
        orderIdRef.current = data.orderId
        startTimeRef.current = Date.now()

        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup)
            // Popup closed — start polling to see if payment went through
            startPolling(data.orderId)
          }
        }, 500)

        // Also poll for status while popup is open
        startPolling(data.orderId)
        return
      }

      // Unlikely for redirect methods — but handle it
      if (data.isPaid) {
        setStep("success")
        setTimeout(() => onComplete("", data.orderId), 2000)
        return
      }

      // No redirect URL? Poll
      startPolling(data.orderId)
    } catch (err) {
      setStep("ready")
      setRedirectMethod(null)
      setError(err instanceof Error ? err.message : "Payment initialization failed")
    }
  }

  // ── Retry ──────────────────────────────────────────────────────────────
  const handleRetry = () => {
    stopPolling()
    destroyComponents()
    initRef.current = false
    setStep("initializing")
    setError("")
    setCardErrors({})
    setOrderId(null)
    setRedirectMethod(null)
    setElapsedTime(0)
    orderIdRef.current = null

    setTimeout(() => {
      initRef.current = false
      window.location.reload()
    }, 50)
  }

  const formatElapsed = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`

  // ── RENDER: Success ─────────────────────────────────────────────────────
  if (step === "success") {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
          <Check className="h-8 w-8 text-green-500" />
        </div>
        <h1 className="text-2xl font-bold text-green-500">Payment Successful!</h1>
        <p className="text-muted-foreground">Your payment has been processed. Redirecting…</p>
      </div>
    )
  }

  // ── RENDER: Failed ──────────────────────────────────────────────────────
  if (step === "failed") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-destructive">Payment Failed</h1>
          <p className="mt-2 text-muted-foreground">{error || "Something went wrong with your payment."}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onCancel}>
            <ChevronLeft className="mr-2 h-4 w-4" /> Go Back
          </Button>
          <Button className="flex-1 gradient-purple text-white" onClick={handleRetry}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  // ── RENDER: Initializing ────────────────────────────────────────────────
  if (step === "initializing") {
    return (
      <div className="space-y-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <h1 className="text-2xl font-bold">Preparing Checkout</h1>
        <p className="text-muted-foreground">Setting up secure payment…</p>
      </div>
    )
  }

  // ── RENDER: Redirecting (PayPal / Google Pay / 3DS) ─────────────────────
  if (step === "redirecting") {
    return (
      <div className="space-y-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <h1 className="text-2xl font-bold">Redirecting…</h1>
        <p className="text-muted-foreground">
          {redirectMethod
            ? `Taking you to ${redirectMethod} to complete your payment.`
            : "Redirecting to complete your payment."}
        </p>
        <p className="text-xs text-muted-foreground">Please do not close this tab.</p>
      </div>
    )
  }

  // ── RENDER: Processing (polling) ────────────────────────────────────────
  if (step === "processing") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            <span className="text-xs font-medium text-green-500">LIVE</span>
            <span className="text-xs text-muted-foreground">Processing</span>
          </div>
          <h1 className="text-2xl font-bold">Processing Payment…</h1>
          <p className="mt-2 text-muted-foreground">Your payment is being confirmed. Please wait.</p>
        </div>

        <GlowCard variant="featured" className="p-6" glow>
          <div className="text-center">
            <RefreshCw className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
            <p className="text-lg font-semibold">{planName}</p>
            <p className="text-3xl font-bold text-primary mt-2">£{priceGbp.toFixed(2)}</p>
            <p className="text-sm text-muted-foreground mt-1">{email}</p>
          </div>
        </GlowCard>

        <GlowCard variant="subtle" className="p-4">
          {elapsedTime >= 12 && (
            <div className="mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-600 dark:text-amber-400">
                If your bank sent a 3D Secure request, please complete it in the popup or new tab.
              </p>
            </div>
          )}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatElapsed(elapsedTime)}</span>
            <span className="flex items-center gap-1 text-green-500">
              <RefreshCw className="h-3 w-3 animate-spin" style={{ animationDuration: "1s" }} /> Checking…
            </span>
          </div>
        </GlowCard>
      </div>
    )
  }

  // ── RENDER: Card form + Express buttons ─────────────────────────────────
  const isSubmitting = step === "submitting"
  const hasCardErrors = Object.keys(cardErrors).length > 0

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold">Complete Payment</h1>
        {isTestMode && (
          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-500 border border-amber-500/30">
            Test Mode
          </span>
        )}
        <p className="mt-1.5 text-sm text-muted-foreground">Choose your preferred payment method</p>
      </div>

      {/* Order Summary */}
      <GlowCard variant="featured" className="p-5">
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Plan</span>
            <span className="font-medium">{planName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Email</span>
            <span className="font-medium text-xs truncate max-w-[200px]">{email}</span>
          </div>
          <div className="border-t border-border pt-3 flex justify-between">
            <span className="font-semibold">Total</span>
            <span className="text-2xl font-bold text-primary">£{priceGbp.toFixed(2)}</span>
          </div>
        </div>
      </GlowCard>

      {/* ── CARD FORM (Mollie Components) ─────────────────────────────── */}
      <GlowCard variant="subtle" className="p-5 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <CreditCard className="h-4 w-4 text-primary" />
          Card Details
        </div>

        <form onSubmit={handleCardSubmit} className="space-y-4">
          {/* Card Holder */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Cardholder Name
            </label>
            <div
              id="mollie-card-holder"
              className={cn(
                "w-full min-h-[44px] px-3 py-3 rounded-lg border bg-secondary/30 transition-colors",
                cardErrors.cardHolder ? "border-destructive" : "border-border/80 focus-within:border-primary"
              )}
            />
            {cardErrors.cardHolder && (
              <p className="flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3 w-3" />{cardErrors.cardHolder}
              </p>
            )}
          </div>

          {/* Card Number */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Card Number
            </label>
            <div
              id="mollie-card-number"
              className={cn(
                "w-full min-h-[44px] px-3 py-3 rounded-lg border bg-secondary/30 transition-colors",
                cardErrors.cardNumber ? "border-destructive" : "border-border/80 focus-within:border-primary"
              )}
            />
            {cardErrors.cardNumber && (
              <p className="flex items-center gap-1 text-xs text-destructive">
                <AlertCircle className="h-3 w-3" />{cardErrors.cardNumber}
              </p>
            )}
          </div>

          {/* Expiry + CVV */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Expiry Date
              </label>
              <div
                id="mollie-expiry-date"
                className={cn(
                  "w-full min-h-[44px] px-3 py-3 rounded-lg border bg-secondary/30 transition-colors",
                  cardErrors.expiryDate ? "border-destructive" : "border-border/80 focus-within:border-primary"
                )}
              />
              {cardErrors.expiryDate && (
                <p className="text-xs text-destructive">{cardErrors.expiryDate}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                CVV / CVC
              </label>
              <div
                id="mollie-cvv"
                className={cn(
                  "w-full min-h-[44px] px-3 py-3 rounded-lg border bg-secondary/30 transition-colors",
                  cardErrors.verificationCode ? "border-destructive" : "border-border/80 focus-within:border-primary"
                )}
              />
              {cardErrors.verificationCode && (
                <p className="text-xs text-destructive">{cardErrors.verificationCode}</p>
              )}
            </div>
          </div>

          {error && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />{error}
            </p>
          )}

          <Button
            type="submit"
            className="w-full gradient-purple text-white h-12 text-base font-medium"
            disabled={isSubmitting || hasCardErrors}
          >
            {isSubmitting ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Processing…</>
            ) : (
              <><Lock className="mr-2 h-4 w-4" />Pay £{priceGbp.toFixed(2)}</>
            )}
          </Button>
        </form>
      </GlowCard>

      {/* ── DIVIDER ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div className="flex-1 border-t border-border" />
        <span className="text-xs text-muted-foreground">or pay with</span>
        <div className="flex-1 border-t border-border" />
      </div>

      {/* ── EXPRESS BUTTONS ─────────────────────────────────────────── */}
      <div className="space-y-2">
        {/* Apple Pay — SDK provides ApplePaySession on all devices;
            on non-Apple devices it shows the "Scan Code with iPhone" QR modal */}
        <button
          type="button"
          onClick={handleApplePay}
          disabled={isSubmitting}
          className={cn(
            "w-full h-12 rounded-lg flex items-center justify-center gap-2",
            "bg-black text-white font-medium text-sm",
            "hover:bg-black/90 transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          {EXPRESS_METHODS[0].icon}
          {EXPRESS_METHODS[0].label}
        </button>

        {/* PayPal */}
        <button
          type="button"
          onClick={() => handleRedirectMethod("paypal", "PayPal")}
          disabled={isSubmitting}
          className={cn(
            "w-full h-12 rounded-lg flex items-center justify-center gap-2",
            "bg-[#FFC439] text-[#003087] font-bold text-sm",
            "hover:bg-[#F0B428] transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          {EXPRESS_METHODS[1].icon}
          {EXPRESS_METHODS[1].label}
        </button>

        {/* Google Pay — redirect to Mollie hosted checkout (no method specified
            so Mollie shows full checkout where Google Pay appears on compatible devices) */}
        <button
          type="button"
          onClick={() => handleRedirectMethod("", "Google Pay")}
          disabled={isSubmitting}
          className={cn(
            "w-full h-12 rounded-lg flex items-center justify-center gap-2",
            "bg-white text-gray-800 border border-gray-300 font-medium text-sm",
            "hover:bg-gray-50 transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          {EXPRESS_METHODS[2].icon}
          {EXPRESS_METHODS[2].label}
        </button>
      </div>

      {/* ── LOCAL PAYMENT METHODS ────────────────────────────────────── */}
      {localMethods.length > 0 && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-border" />
            <span className="text-xs text-muted-foreground">local methods</span>
            <div className="flex-1 border-t border-border" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {localMethods.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => handleRedirectMethod(m.id, m.description)}
                disabled={isSubmitting}
                className={cn(
                  "h-12 rounded-lg flex items-center justify-center gap-2 px-3",
                  "bg-secondary/60 border border-border text-sm font-medium",
                  "hover:bg-secondary/80 transition-colors",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {m.image && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={m.image} alt={m.description} className="h-5 w-auto" />
                )}
                <span className="truncate">{m.description}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── TRUST BADGES ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-center gap-5 text-xs text-muted-foreground pt-1">
        <span className="flex items-center gap-1.5">
          <Shield className="h-4 w-4 text-green-500/90" />
          PCI Compliant
        </span>
        <span className="flex items-center gap-1.5">
          <Lock className="h-4 w-4 text-green-500/90" />
          256-bit Encryption
        </span>
      </div>

      {/* Accepted cards */}
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span>Visa</span>
        <span>Mastercard</span>
        <span>Amex</span>
        <span>Apple Pay</span>
        <span>Google Pay</span>
        <span>PayPal</span>
      </div>

      {isTestMode && (
        <p className="text-center text-xs text-amber-600 dark:text-amber-400">
          Test mode — use card <span className="font-mono">4342 7624 9745 5174</span>, 12/2025, 111
        </p>
      )}

      {/* Crypto payment option */}
      {onSwitchToCrypto && (
        <button
          type="button"
          onClick={onSwitchToCrypto}
          className="w-full py-3 px-4 rounded-lg border border-border/50 bg-secondary/30 hover:bg-secondary/50 transition-colors text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-2"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.546z"/><path d="M17.2 10.56c.24-1.6-1.04-2.46-2.78-3.03l.57-2.28-1.39-.35-.55 2.22c-.37-.09-.74-.18-1.12-.26l.55-2.23-1.39-.35-.57 2.28c-.3-.07-.6-.14-.89-.21l-1.92-.48-.37 1.49s1.04.24 1.02.25c.57.14.67.52.65.82l-.65 2.63c.04.01.09.02.14.04l-.14-.04-.92 3.68c-.07.17-.24.42-.63.33.01.02-1.02-.25-1.02-.25l-.7 1.6 1.81.45c.34.09.67.17 1 .25l-.57 2.31 1.39.35.57-2.28c.38.1.75.2 1.12.29l-.57 2.27 1.39.35.57-2.3c2.38.45 4.17.27 4.92-1.89.6-1.74-.03-2.74-1.29-3.4.91-.21 1.6-.82 1.79-2.07z" fill="#fff"/></svg>
          Pay with Cryptocurrency (+£5 fee)
        </button>
      )}

      {/* Back button */}
      <Button variant="outline" className="w-full" onClick={onCancel} disabled={isSubmitting}>
        <ChevronLeft className="mr-2 h-4 w-4" />
        Back to Plan Selection
      </Button>

    </div>
  )
}
